import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { providePrimeNG } from 'primeng/config';

// Kuba UI Kit foundations.
//
// Two layers, deliberately separated:
//   * Brand hue is tenant-owned. `primary` resolves through the --color-primary-*
//     variables ThemeService writes, so Kuba IK (ThemeId.DEFAULT) gets the purple
//     spelled out in styles/_variables.scss and every other tenant keeps its own.
//   * Everything else is the shared structural layer -- radius, form metrics,
//     neutral surfaces, elevation, grid density. Identical for every tenant.
//
// Colour, radius and spacing change here, not in feature SCSS. Page SCSS may set
// layout only; if a component resists, add a component token below.
export const kubaPrimeTheme = definePreset(Aura, {
  primitive: {
    // UI Kit radius scale. Stated in px so the 14px rem baseline cannot rescale it.
    borderRadius: { xs: '5px', sm: '7px', md: '9px', lg: '12px', xl: '14px' }
  },
  semantic: {
    primary: {
      // The legacy light shades are too saturated for Aura's selected surfaces.
      ...Object.fromEntries(
        [
          [50, 5],
          [100, 10],
          [200, 20],
          [300, 40],
          [400, 70]
        ].map(([shade, percentage]) => [
          shade,
          `color-mix(in srgb, var(--color-primary-500) ${percentage}%, white)`
        ])
      ),
      ...Object.fromEntries(
        [500, 600, 700, 800, 900].map(shade => [
          shade,
          `var(--color-primary-${shade})`
        ])
      ),
      950: 'color-mix(in srgb, var(--color-primary-500) 25%, black)'
    },
    focusRing: { width: '2px', offset: '2px' },
    // UI Kit form metrics: 0.75rem / 0.5rem against the 14px root.
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.5rem',
      focusRing: {
        width: '2px',
        style: 'solid',
        color: '{primary.color}',
        offset: '2px',
        shadow: 'none'
      }
    },
    // UI Kit elevation: card 0 1 3, overlay 0 14 34.
    overlay: {
      select: { shadow: 'var(--kuba-shadow-overlay)' },
      popover: { shadow: 'var(--kuba-shadow-overlay)' },
      modal: { shadow: 'var(--kuba-shadow-overlay)' },
      navigation: { shadow: 'var(--kuba-shadow-overlay)' }
    },
    colorScheme: {
      light: {
        primary: {
          color: '{primary.500}',
          contrastColor: 'var(--on-color-primary-500)',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}'
        },
        highlight: {
          background: '{primary.50}',
          focusBackground: '{primary.100}',
          color: '{primary.700}',
          focusColor: '{primary.800}'
        },
        // Warm neutral ramp from the UI Kit. Not brand colour -- every tenant shares it.
        surface: {
          0: '#ffffff',
          50: '#fafaf9',
          100: '#f5f5f4',
          200: '#e7e5e4',
          300: '#d6d3d1',
          400: '#a8a29e',
          500: '#78716c',
          600: '#57534e',
          700: '#44403c',
          800: '#292524',
          900: '#1c1917',
          950: '#0c0a09'
        },
        text: {
          color: '{surface.900}',
          hoverColor: '{surface.900}',
          mutedColor: '{surface.500}',
          hoverMutedColor: '{surface.600}'
        },
        content: { borderColor: '{surface.200}' }
      }
    }
  },
  components: {
    datatable: {
      // UI Kit grid density (balanced). PrimeNG's own `small` size carries compact.
      headerCell: { padding: '9px 14px', background: '#faf9f8' },
      bodyCell: { padding: '10px 14px' },
      header: { padding: '0.875rem' },
      footer: { padding: '0.75rem 0.875rem' }
    },
    card: {
      root: { shadow: 'var(--kuba-shadow-card)' },
      title: { fontWeight: '600' }
    },

    // Overlays. The kit allows four kinds, separated by weight, and gives them all
    // the same paper: xl radius, the overlay elevation, and a 600-weight title.
    dialog: {
      root: { borderRadius: '{border.radius.xl}', shadow: 'var(--kuba-shadow-overlay)' },
      header: { padding: '1.25rem 1.25rem 0.875rem' },
      title: { fontSize: '1.125rem', fontWeight: '600' },
      content: { padding: '0 1.25rem 1.25rem' },
      footer: { padding: '0.875rem 1.25rem 1.25rem', gap: '0.5rem' }
    },
    confirmdialog: { icon: { size: '1.5rem' }, content: { gap: '0.875rem' } },
    drawer: {
      root: { shadow: 'var(--kuba-shadow-overlay)' },
      header: { padding: '1.25rem 1.25rem 0.875rem' },
      title: { fontSize: '1.125rem', fontWeight: '600' },
      content: { padding: '0 1.25rem 1.25rem' }
    },
    popover: {
      root: { borderRadius: '{border.radius.lg}', shadow: 'var(--kuba-shadow-overlay)' }
    },
    menu: {
      root: { borderRadius: '{border.radius.lg}', shadow: 'var(--kuba-shadow-overlay)' },
      list: { padding: '0.375rem', gap: '2px' },
      item: { borderRadius: '{border.radius.sm}', padding: '0.5rem 0.625rem' }
    },
    toast: {
      root: { borderRadius: '{border.radius.lg}' },
      summary: { fontWeight: '600', fontSize: '0.9375rem' },
      detail: { fontWeight: '400', fontSize: '0.875rem' }
    },
    tooltip: { root: { borderRadius: '{border.radius.sm}' } },

    // Status is a badge, never a colour alone: text plus tint.
    tag: {
      root: {
        fontSize: '0.75rem',
        fontWeight: '600',
        padding: '0.25rem 0.5rem',
        borderRadius: '{border.radius.sm}'
      }
    },
    button: {
      root: {
        borderRadius: '{border.radius.md}',
        paddingX: '0.875rem',
        paddingY: '0.5rem',
        gap: '0.5rem'
      }
    }
  }
});

export function provideKubaPrimeNG() {
  return providePrimeNG({
    // PrimeNG 21 defaults overlays to 'self', which renders a select panel inside its
    // own container. Inside a scrolling table the container clips the panel and its
    // scrollbar paints over the options, so they cannot be clicked. Appending to body
    // is the pre-upgrade behaviour and applies to every overlay at once.
    overlayAppendTo: 'body',
    // A body-level overlay stacks from its base, so PrimeNG's default of 1000 puts
    // select panels and menus behind ngx-bootstrap modals (.modal is 1050) and they
    // cannot be opened there. 1060 clears the modal and stays below Bootstrap
    // popovers (1070) and the navigation panels (1100). Overlays inside a PrimeNG
    // dialog still stack above it. This object replaces the defaults, so all keys stay.
    zIndex: {
      modal: 1100,
      overlay: 1060,
      menu: 1060,
      tooltip: 1100
    },
    theme: {
      preset: kubaPrimeTheme,
      // Aura is unlayered; the retired PrimeNG skin is no longer loaded.
      options: { darkModeSelector: false }
    }
  });
}
